import { useCallback, useEffect, useState } from "react";
import { CircleMarker, MapContainer, Polyline, Popup, TileLayer } from "react-leaflet";
import PolicyLink from "@/components/PolicyLink";
import "leaflet/dist/leaflet.css";
import { format } from "date-fns";
import { Loader2, MapPin, RefreshCw, Users } from "lucide-react";
import { toast } from "@/components/ui/sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import api, { formatApiError } from "@/lib/api";

const DEFAULT_CENTER = [51.5074, -0.1278];
const REFRESH_MS = 30000;

const fmt = (v) => {
  if (!v) return "—";
  try {
    return format(new Date(v), "dd MMM, HH:mm");
  } catch {
    return "—";
  }
};

const duration = (start, end) => {
  if (!start) return "—";
  const ms = new Date(end || Date.now()) - new Date(start);
  const mins = Math.max(0, Math.round(ms / 60000));
  return `${Math.floor(mins / 60)}h ${mins % 60}m`;
};

export default function LiveStaffPage() {
  const [live, setLive] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [track, setTrack] = useState({ shiftId: null, points: [] });

  const load = useCallback(async () => {
    try {
      const [liveRes, histRes] = await Promise.all([
        api.get("/attendance/live"),
        api.get("/attendance/history?days=7"),
      ]);
      setLive(liveRes.data.items);
      setHistory(histRes.data.items);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, REFRESH_MS);
    return () => clearInterval(t);
  }, [load]);

  const showTrack = async (shiftId) => {
    try {
      const { data } = await api.get(`/attendance/${shiftId}/track`);
      setTrack({ shiftId, points: data.items.map((p) => [p.lat, p.lng]) });
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const located = live.filter((s) => s.last_location);
  const center = located.length
    ? [located.reduce((a, s) => a + s.last_location.lat, 0) / located.length,
       located.reduce((a, s) => a + s.last_location.lng, 0) / located.length]
    : DEFAULT_CENTER;

  return (
    <div data-testid="live-staff-page" className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">Live Staff</h1>
          <PolicyLink module="gps" />
          <p className="text-sm text-slate-600 mt-1">
            {live.length} staff on shift · positions refresh every 30 seconds
          </p>
        </div>
        <Button variant="outline" className="h-11" data-testid="live-staff-refresh-button" onClick={load}>
          <RefreshCw className="h-4 w-4 mr-2" /> Refresh
        </Button>
      </div>

      <Card>
        <CardContent className="p-0">
          <div data-testid="live-staff-map" className="h-[420px] w-full overflow-hidden rounded-lg">
            <MapContainer center={center} zoom={11} style={{ height: "100%", width: "100%" }}>
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              {located.map((s) => (
                <CircleMarker
                  key={s.id}
                  center={[s.last_location.lat, s.last_location.lng]}
                  radius={10}
                  pathOptions={{ color: "#0891b2", fillColor: "#10b981", fillOpacity: 0.9, weight: 3 }}
                >
                  <Popup>
                    <div className="text-sm">
                      <p className="font-semibold">{s.user_name}</p>
                      <p>{s.staff_number || ""}</p>
                      <p>On shift since {fmt(s.signed_in_at)}</p>
                      <p>Last seen {fmt(s.last_location.captured_at)}</p>
                      {s.last_location.accuracy != null && <p>Accuracy ±{Math.round(s.last_location.accuracy)}m</p>}
                    </div>
                  </Popup>
                </CircleMarker>
              ))}
              {track.points.length > 1 && (
                <Polyline positions={track.points} pathOptions={{ color: "#0891b2", weight: 4, opacity: 0.8 }} />
              )}
            </MapContainer>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <MapPin className="h-5 w-5 text-primary" /> On shift now
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center gap-2 text-slate-600" data-testid="live-staff-loading">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading…
            </div>
          ) : live.length === 0 ? (
            <p className="py-8 text-center text-sm text-slate-600" data-testid="live-staff-empty">
              No staff are currently signed in to a shift.
            </p>
          ) : (
            <div className="space-y-2" data-testid="live-staff-list">
              {live.map((s) => (
                <div
                  key={s.id}
                  data-testid={`live-staff-row-${s.id}`}
                  className="flex flex-wrap items-center gap-3 rounded-lg border border-border p-3"
                >
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-slate-900">{s.user_name}</p>
                    <p className="text-xs text-slate-500">
                      {s.staff_number || "—"} · since {fmt(s.signed_in_at)} · {duration(s.signed_in_at)}
                    </p>
                  </div>
                  {s.last_location ? (
                    <Badge className="bg-emerald-100 text-emerald-800 border-transparent" data-testid={`live-staff-gps-${s.id}`}>
                      GPS live · {fmt(s.last_location.captured_at)}
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="text-amber-700 border-amber-300" data-testid={`live-staff-nogps-${s.id}`}>
                      No location
                    </Badge>
                  )}
                  <Button variant="outline" className="h-9 text-xs" data-testid={`live-staff-track-${s.id}`} onClick={() => showTrack(s.id)}>
                    Show route
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Users className="h-5 w-5 text-primary" /> Shift history (last 7 days)
          </CardTitle>
        </CardHeader>
        <CardContent>
          {history.length === 0 ? (
            <p className="py-8 text-center text-sm text-slate-600" data-testid="shift-history-empty">No shifts recorded yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="shift-history-table">
                <thead>
                  <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-slate-500">
                    <th className="py-2 pr-4">Staff</th>
                    <th className="py-2 pr-4">Signed in</th>
                    <th className="py-2 pr-4">Signed out</th>
                    <th className="py-2 pr-4">Duration</th>
                    <th className="py-2 pr-4">Status</th>
                    <th className="py-2">Route</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map((s) => (
                    <tr key={s.id} className="border-b border-border/60" data-testid={`shift-history-row-${s.id}`}>
                      <td className="py-2.5 pr-4">
                        <p className="font-medium text-slate-900">{s.user_name}</p>
                        <p className="text-xs text-slate-500">{s.staff_number || "—"}</p>
                      </td>
                      <td className="py-2.5 pr-4">{fmt(s.signed_in_at)}</td>
                      <td className="py-2.5 pr-4">{fmt(s.signed_out_at)}</td>
                      <td className="py-2.5 pr-4">{duration(s.signed_in_at, s.signed_out_at)}</td>
                      <td className="py-2.5 pr-4">
                        {s.status === "active" ? (
                          <Badge className="bg-emerald-100 text-emerald-800 border-transparent">On shift</Badge>
                        ) : (
                          <Badge variant="outline">Closed</Badge>
                        )}
                      </td>
                      <td className="py-2.5">
                        <Button variant="ghost" className="h-8 text-xs" data-testid={`shift-history-track-${s.id}`} onClick={() => showTrack(s.id)}>
                          Show route
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
